<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * the q-card-section branch, and collapse effectiveProps to { ...attrs }.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Card content section wrapper with optional horizontal layout.
 *
 * @status internal
 * @group cards-dialogs
 */
defineOptions({ name: 'HrCardSection', inheritAttrs: false });

const props = defineProps({
  /**
   * Lays out the section's children in a row alongside sibling sections.
   * Mirrors q-card-section's horizontal prop.
   */
  horizontal: {
    type: Boolean,
    default: false,
  },
  /**
   * HTML tag to render the section as. Mirrors q-card-section's tag prop.
   */
  tag: {
    type: String,
    default: 'div',
  },
  /**
   * Legacy props passed through to q-card-section for backwards compatibility with the legacy
   * theme. Ignored when a design system theme is active.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-card-section',
  props.horizontal && 'hr-card-section--horiz',
]);

const effectiveProps = computed(() => {
  if (hasTheme.value) {
    return { ...attrs };
  }
  return { ...props.legacyProps, ...attrs };
});
</script>
<template>
  <component
    :is="tag"
    v-if="hasTheme"
    :class="classes"
    v-bind="effectiveProps"
  >
    <!-- @slot default Section content — text, form fields, or other card body elements. -->
    <slot />
  </component>
  <q-card-section
    v-else
    :horizontal="horizontal"
    :tag="tag"
    v-bind="effectiveProps"
  >
    <slot />
  </q-card-section>
</template>
<style lang="scss" scoped>

/* Only applies to light/dark themes */
.hr-card-section {
  // 16px matches Quasar's q-card-section default; replace with a spacing token when available
  padding: 16px;
}

.hr-card-section--horiz {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}
</style>
